import Panel from "@ui/panel/Panel";
import NormalPanel from "@ui/panel/NormalPanel";
import { ComponentProps, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { S_CONFIRM } from "../popup/general/ConfirmDialog";
import { S_SELECT_ITEMS_POPUP } from "../popup/SelectItemsPopup";
import { S_SELECT_COUNTRIES_POPUP } from "../popup/SelectCountriesPopup";

const openPanels: symbol[] = [];

// Keep provider dialogs in opening order, independently of their place in React's tree.
const ProviderModal = ({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) => {
	const id = useRef(Symbol("provider-dialog"));
	const close = useRef(onClose);
	close.current = onClose;
	const confirmation = S_CONFIRM.use();
	const items = S_SELECT_ITEMS_POPUP.use();
	const countries = S_SELECT_COUNTRIES_POPUP.use();
	const auxiliary = useRef({ confirmation, items, countries });
	auxiliary.current = { confirmation, items, countries };

	useEffect(() => {
		const token = id.current;
		openPanels.push(token);
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || openPanels.at(-1) !== token) return;
			// The shared Panel listeners otherwise close every mounted dialog.
			event.preventDefault();
			event.stopImmediatePropagation();
			// These auxiliary dialogs live in GlobalView's higher (1001) layer.
			if (auxiliary.current.confirmation) S_CONFIRM.invoke(null);
			else if (auxiliary.current.items) {
				auxiliary.current.items.onClose();
				S_SELECT_ITEMS_POPUP.invoke(null);
			} else if (auxiliary.current.countries) S_SELECT_COUNTRIES_POPUP.invoke(null);
			else close.current?.();
		};
		document.addEventListener("keydown", onKeyDown, true);
		return () => {
			openPanels.splice(openPanels.indexOf(token), 1);
			document.removeEventListener("keydown", onKeyDown, true);
		};
	}, []);

	return createPortal(
		<div style={{ position: "relative", zIndex: 1000 }} onClick={event => event.stopPropagation()}>
			{children}
		</div>,
		document.body,
	);
};

export default function ProviderPanel(props: ComponentProps<typeof Panel>) {
	return props.decorator === "absolute" ? <ProviderModal onClose={props.onClose}><Panel {...props} /></ProviderModal> : <Panel {...props} />;
}

export function ProviderNormalPanel(props: ComponentProps<typeof NormalPanel>) {
	return props.decorator === "absolute" ? <ProviderModal onClose={props.onClose}><NormalPanel {...props} /></ProviderModal> : <NormalPanel {...props} />;
}
